import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_ayurveda-care-27/artifacts/qmolq6en_Gemini_Generated_Image_xzba4zxzba4zxzba.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Enter username and password");
      return;
    }
    setBusy(true);
    try {
      await login(username.trim(), password);
      toast.success("Welcome to Badaris Lifecare");
      navigate("/select-branch", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-warm-sand" data-testid="login-page">
      {/* Hero */}
      <div className="relative hidden lg:block grain-overlay">
        <img
          src={HERO_IMG}
          alt="Ayurveda interior"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#1A4331]/55" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-4 py-2.5 shadow-lg">
              <BrandLogo size="h-12" />
            </div>
          </div>

          <div className="max-w-md space-y-5">
            <div className="text-xs uppercase tracking-[0.28em] text-[#E5A93D]">
              Est. Badarinath Ayurveda Hospital
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
              Where classical Ayurveda meets a modern hospital.
            </h1>
            <p className="text-white/80 leading-relaxed max-w-sm">
              A unified system for six centres — patient care, prescriptions, pharmacy and
              billing, all in one warm workspace.
            </p>
          </div>

          <div className="text-xs text-white/60">
            © {new Date().getFullYear()} Badarinath Ayurveda Hospital · Staff portal
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-8 flex justify-center">
            <BrandLogo size="h-14" />
          </div>

          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Staff Sign-in
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-forest mb-2">
            Welcome back.
          </h2>
          <p className="text-[#5C5652] mb-8 text-sm">
            Please sign in with your role credentials to continue.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
                Username
              </label>
              <div className="relative">
                <UserIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" strokeWidth={1.5} />
                <input
                  data-testid="login-username-input"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g., adminlog"
                  className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand text-[#2A2624] placeholder:text-[#5C5652]/60 focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" strokeWidth={1.5} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand text-[#2A2624] placeholder:text-[#5C5652]/60 focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="group w-full h-11 rounded-lg bg-forest text-white font-semibold flex items-center justify-center gap-2 hover:bg-forest-hover transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-sand text-xs text-[#5C5652] leading-relaxed">
            <div className="font-semibold text-[#2A2624] mb-1">Need access?</div>
            Contact your branch administrator or the hospital manager to receive your role credentials.
          </div>
        </div>
      </div>
    </div>
  );
}
